import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Lock, ChevronLeft, ChevronRight, Check, Truck, MapPin, CreditCard, ShieldCheck, ArrowRight, Edit3, Package, Plus, X } from 'lucide-react';
import { useBag } from '../../contexts/BagContext';
import { useRegion } from '../../contexts/RegionContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient';
import { FaCreditCard, FaUniversity } from 'react-icons/fa';

import FozzilPay from '../../utils/FozzilPaySDK';
import FozzilPayCheckout from '../FozzilPayCheckout';

const CheckoutPage = () => {
  const { items, subtotal, clearBag } = useBag();
  const { formatPrice, region } = useRegion();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('shipping');
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '', lastName: '', email: user?.email || '', phone: '',
    address: '', apartment: '', city: '', state: '', zip: '', country: region.name,
  });
  const [deliveryMethod, setDeliveryMethod] = useState('standard');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardInfo, setCardInfo] = useState({ number: '', expiry: '', cvv: '', name: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [savedAddress, setSavedAddress] = useState(false);
  const [showFozzilPay, setShowFozzilPay] = useState(false);

  // Address management
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressIdx, setSelectedAddressIdx] = useState(-1);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(true);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [saveNewToAccount, setSaveNewToAccount] = useState(true);
  const [profileRef, setProfileRef] = useState(null);

  const expressShippingCost = Math.round(subtotal * 0.02);
  const shipping = deliveryMethod === 'express' ? expressShippingCost : 0;
  const total = subtotal + shipping;

  // Load billing addresses from FOZZIL ID profile + secondary addresses
  useEffect(() => {
    const loadAddresses = async () => {
      if (!user?.id) return;
      try {
        // Fetch profile (billing address)
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error("Profile fetch error:", error);
        }

        setProfileRef(profile);
        const allAddresses = [];

        // 1. Primary billing address from profile
        if (profile?.address_line) {
          allAddresses.push({
            id: 'profile-primary',
            firstName: profile.first_name || '',
            lastName: profile.last_name || '',
            phone: profile.phone || '',
            address: profile.address_line || '',
            apartment: '',
            city: profile.city || '',
            state: profile.state || '',
            zip: profile.pincode || '',
            country: profile.country_region || region.name,
            isDefault: true,
            source: 'billing', // Tag to identify this is from billing
          });
        }

        // 2. Secondary billing addresses from user_addresses table
        try {
          const addressResult = await FozzilPay.getAddresses(user.id);
          if (addressResult.success && addressResult.data?.length > 0) {
            addressResult.data.forEach(addr => {
              allAddresses.push({
                id: `secondary-${addr.id}`,
                dbId: addr.id,
                firstName: addr.first_name || profile?.first_name || '',
                lastName: addr.last_name || profile?.last_name || '',
                phone: profile?.phone || '',
                address: addr.address_line || '',
                apartment: '',
                city: addr.city || '',
                state: addr.state || '',
                zip: addr.pincode || '',
                country: addr.country_region || region.name,
                isDefault: false,
                source: 'billing-secondary',
              });
            });
          }
        } catch (err) {
          // Secondary addresses unavailable, continue
        }

        // 3. Extra shipping addresses saved from checkout
        const shippingAddrs = profile?.shipping_addresses || [];
        shippingAddrs.forEach(addr => {
          allAddresses.push({
            ...addr,
            source: 'shipping',
          });
        });

        setSavedAddresses(allAddresses);

        if (allAddresses.length > 0) {
          const defaultIdx = allAddresses.findIndex(a => a.isDefault);
          const idx = defaultIdx >= 0 ? defaultIdx : 0;
          setSelectedAddressIdx(idx);
          applyAddressFromList(allAddresses[idx], profile);
        } else if (profile) {
          // No addresses but profile exists — pre-fill name/phone
          setShippingInfo(prev => ({
            ...prev,
            firstName: profile.first_name || '',
            lastName: profile.last_name || '',
            email: user?.email || '',
            phone: profile.phone || '',
            country: profile.country_region || region.name,
          }));
          setShowAddressForm(true);
        } else {
          setShowAddressForm(true);
        }
      } catch (err) {
        console.error('Error loading addresses:', err);
        setShowAddressForm(true);
      } finally {
        setIsLoadingAddresses(false);
      }

      // Also try to preload primary payment method from their FOZZIL Account
      try {
        const methodsResult = await FozzilPay.getPaymentMethods(user.id);
        if (methodsResult.success && methodsResult.data?.length > 0) {
          const primaryMethod = methodsResult.data.find(m => m.is_primary) || methodsResult.data[0];
          // map backend string to our frontend component selection enum
          if (primaryMethod.payment_type === 'UPI') setPaymentMethod('upi');
          else if (primaryMethod.payment_type === 'Netbanking' || primaryMethod.payment_type === 'Net Banking') setPaymentMethod('bank');
          else if (primaryMethod.payment_type === 'Card') setPaymentMethod('card');
          // (if wallet, leave as card/default as we don't explicitly list wallet here yet)
        }
      } catch (err) {
        console.warn('Could not auto-load saved payment methods', err);
      }
    };

    loadAddresses();
  }, [user?.id]);

  // Enforce UPI limits (₹1,00,000 threshold). If order total >= 99,000 we hide UPI.
  useEffect(() => {
    if (total > 99000 && paymentMethod === 'upi') {
      setPaymentMethod('card');
    }
  }, [total, paymentMethod]);

  useEffect(() => {
    if (items.length === 0 && step !== 'success') {
      navigate('/shop/bag');
    }
  }, [items, step, navigate]);

  useEffect(() => {
    if (user?.email) {
      setShippingInfo(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  const applyAddressFromList = (addr, profile) => {
    setShippingInfo({
      firstName: addr.firstName || profile?.first_name || '',
      lastName: addr.lastName || profile?.last_name || '',
      email: user?.email || '',
      phone: addr.phone || profile?.phone || '',
      address: addr.address || '',
      apartment: addr.apartment || '',
      city: addr.city || '',
      state: addr.state || '',
      zip: addr.zip || '',
      country: addr.country || profile?.country_region || region.name,
    });
  };

  const handleSelectAddress = (idx) => {
    setSelectedAddressIdx(idx);
    setShowAddressForm(false);
    applyAddressFromList(savedAddresses[idx], profileRef);
  };

  const handleAddNewAddress = () => {
    setSelectedAddressIdx(-1);
    setShowAddressForm(true);
    setShippingInfo({
      firstName: profileRef?.first_name || '', lastName: profileRef?.last_name || '',
      email: user?.email || '', phone: profileRef?.phone || '',
      address: '', apartment: '', city: '', state: '', zip: '',
      country: profileRef?.country_region || region.name,
    });
  };

  const saveAddressToSupabase = async (addressData) => {
    if (!user?.id) return;
    try {
      setIsSavingAddress(true);
      const newAddress = {
        id: Date.now().toString(),
        firstName: addressData.firstName,
        lastName: addressData.lastName,
        phone: addressData.phone,
        address: addressData.address,
        apartment: addressData.apartment,
        city: addressData.city,
        state: addressData.state,
        zip: addressData.zip,
        country: addressData.country,
        isDefault: false,
        createdAt: new Date().toISOString(),
      };

      // Save to shipping_addresses in profile
      const existingShipping = savedAddresses.filter(a => a.source === 'shipping');
      const updatedShipping = [...existingShipping, newAddress];

      const { error } = await supabase
        .from('profiles')
        .update({ shipping_addresses: updatedShipping })
        .eq('id', user.id);

      if (!error) {
        const newEntry = { ...newAddress, source: 'shipping' };
        const updated = [...savedAddresses, newEntry];
        setSavedAddresses(updated);
        setSelectedAddressIdx(updated.length - 1);
        setShowAddressForm(false);
      } else {
        console.error('Error saving address to Supabase:', error);
        // Temporary fallback: save to local session so checkout can proceed
        const newEntry = { ...newAddress, source: 'shipping' };
        const updated = [...savedAddresses, newEntry];
        setSavedAddresses(updated);
        setSelectedAddressIdx(updated.length - 1);
        setShowAddressForm(false);
      }
    } catch (err) {
      console.error('Error saving address:', err);
    } finally {
      setIsSavingAddress(false);
    }
  };

  const deleteAddressFromSupabase = async (idx) => {
    if (!user?.id) return;
    const addr = savedAddresses[idx];
    // Don't allow deleting billing primary
    if (addr.source === 'billing') return;

    try {
      if (addr.source === 'shipping') {
        const updatedShipping = savedAddresses
          .filter(a => a.source === 'shipping')
          .filter(a => a.id !== addr.id);

        const { error } = await supabase
          .from('profiles')
          .update({ shipping_addresses: updatedShipping })
          .eq('id', user.id);

        if (!error) {
          const updated = savedAddresses.filter((_, i) => i !== idx);
          setSavedAddresses(updated);
          if (selectedAddressIdx === idx) {
            if (updated.length > 0) { setSelectedAddressIdx(0); applyAddressFromList(updated[0], profileRef); }
            else { setSelectedAddressIdx(-1); setShowAddressForm(true); }
          } else if (selectedAddressIdx > idx) {
            setSelectedAddressIdx(selectedAddressIdx - 1);
          }
        }
      }
    } catch (err) {
      console.error('Error deleting address:', err);
    }
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    // If showing new address form and user wants to save it
    if (showAddressForm && saveNewToAccount) {
      await saveAddressToSupabase(shippingInfo);
    }
    setSavedAddress(true);
    setStep('delivery');
    window.scrollTo(0, 0);
  };

  const handleDeliverySubmit = () => {
    setStep('payment');
    window.scrollTo(0, 0);
  };

  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    setStep('review');
    window.scrollTo(0, 0);
  };

  const handlePlaceOrder = () => {
    setShowFozzilPay(true);
  };

  const handlePaymentSuccess = async (paymentData) => {
    setShowFozzilPay(false);
    setStep('processing');
    setIsProcessing(true);
    window.scrollTo(0, 0);

    try {
      const generatedOrderNumber = paymentData?.txnid || 'FZ-' + Math.random().toString(36).substring(2, 10).toUpperCase();
      const actualPaymentMethod = paymentData?.mode || (paymentMethod === 'card' ? 'Card' : paymentMethod === 'upi' ? 'UPI' : 'Net Banking');
      const estimatedDelivery = new Date(Date.now() + (deliveryMethod === 'express' ? 3 : 7) * 86400000);

      // Save order to Supabase
      const orderData = {
        order_number: generatedOrderNumber,
        user_id: user.id,
        items: items.map(item => ({
          bagId: item.bagId,
          name: item.name,
          image: item.image,
          price: item.price,
          quantity: item.quantity,
          config: item.config || [],
        })),
        subtotal: subtotal,
        shipping_cost: shipping,
        total: total,
        currency: region.currency || 'INR',
        shipping_address: {
          firstName: shippingInfo.firstName,
          lastName: shippingInfo.lastName,
          email: shippingInfo.email,
          phone: shippingInfo.phone,
          address: shippingInfo.address,
          apartment: shippingInfo.apartment,
          city: shippingInfo.city,
          state: shippingInfo.state,
          zip: shippingInfo.zip,
          country: shippingInfo.country,
        },
        delivery_method: deliveryMethod,
        estimated_delivery: estimatedDelivery.toISOString().split('T')[0],
        payment_method: actualPaymentMethod,
        payment_status: 'completed',
        status: 'confirmed',
        status_history: [
          { status: 'confirmed', timestamp: new Date().toISOString(), note: 'Order placed successfully' },
        ],
      };

      const { error: orderError } = await supabase
        .from('store_orders')
        .insert([orderData]);

      if (orderError) {
        console.error('Error saving order:', orderError);
      }

      // Send order confirmation email via backend
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://fpaybeshadesecure.fozzilindustries.workers.dev';
        await fetch(`${backendUrl}/api/store/order-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            targetEmail: shippingInfo.email,
            orderNumber: generatedOrderNumber,
            customerName: `${shippingInfo.firstName} ${shippingInfo.lastName}`,
            items: items.map(item => ({
              name: item.name,
              price: item.price,
              quantity: item.quantity,
            })),
            subtotal,
            shipping: shipping,
            total,
            currency: region.currency || 'INR',
            paymentMethod: actualPaymentMethod,
            deliveryMethod: deliveryMethod === 'express' ? 'Express (2–3 business days)' : 'Standard (5–7 business days)',
            estimatedDelivery: estimatedDelivery.toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
            shippingAddress: `${shippingInfo.address}${shippingInfo.apartment ? ', ' + shippingInfo.apartment : ''}, ${shippingInfo.city}, ${shippingInfo.state} ${shippingInfo.zip}, ${shippingInfo.country}`,
          }),
        });
      } catch (emailErr) {
        console.warn('Order email notification failed:', emailErr);
      }

      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 2500));

      setOrderNumber(generatedOrderNumber);
      setIsProcessing(false);
      setStep('success');
      clearBag();
    } catch (err) {
      console.error('Order placement error:', err);
      // Even if DB save fails, still show success (payment was already processed)
      setOrderNumber('FZ-' + Math.random().toString(36).substring(2, 10).toUpperCase());
      setIsProcessing(false);
      setStep('success');
      clearBag();
    }
  };

  const StepIndicator = () => {
    const steps = [
      { id: 'shipping', label: 'Shipping' },
      { id: 'delivery', label: 'Delivery' },
      { id: 'payment', label: 'Payment' },
      { id: 'review', label: 'Review' },
    ];
    const currentIdx = steps.findIndex(s => s.id === step);
    const effectiveIdx = step === 'processing' || step === 'success' ? 4 : currentIdx;

    return (
      <div className="flex items-center justify-center gap-2 mb-10">
        {steps.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              onClick={() => i < effectiveIdx && setStep(s.id)}
              disabled={i >= effectiveIdx}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors bg-transparent border-none ${
                i < effectiveIdx ? 'text-[#065DC0] cursor-pointer hover:underline' :
                i === effectiveIdx ? 'text-[#1d1d1f] cursor-default' :
                'text-[#86868b] cursor-default'
              }`}
            >
              {i < effectiveIdx ? (
                <div className="w-5 h-5 rounded-full bg-[#065DC0] flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              ) : (
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                  i === effectiveIdx ? 'border-2 border-[#065DC0] text-[#065DC0]' : 'border border-[#d2d2d7] text-[#86868b]'
                }`}>{i + 1}</div>
              )}
              <span className="hidden md:inline">{s.label}</span>
            </button>
            {i < steps.length - 1 && (
              <div className={`w-8 md:w-16 h-[2px] rounded-full ${i < effectiveIdx ? 'bg-[#065DC0]' : 'bg-[#d2d2d7]'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const OrderSummary = () => (
    <div className="bg-[#f5f5f7] rounded-[32px] p-6 sticky top-[100px]">
      <h3 className="text-base font-bold text-[#1d1d1f] mb-4">Order Summary</h3>
      <div className="space-y-4 mb-4">
        {items.map(item => (
          <div key={item.bagId} className="flex gap-3">
            <img src={item.image} alt={item.name} className="w-14 h-14 object-contain rounded-lg bg-white p-1" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#1d1d1f] truncate">{item.name}</p>
              <p className="text-xs text-[#6e6e73]">Qty: {item.quantity}</p>
            </div>
            <p className="text-sm font-medium text-[#1d1d1f] shrink-0">{formatPrice(item.price * item.quantity)}</p>
          </div>
        ))}
      </div>
      <div className="border-t border-[#d2d2d7] pt-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-[#6e6e73]">Subtotal</span>
          <span className="text-[#1d1d1f] font-medium">{formatPrice(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#6e6e73]">Shipping</span>
          <span className="text-[#1d1d1f] font-medium">{shipping === 0 ? 'FREE' : formatPrice(shipping)}</span>
        </div>
        <div className="flex justify-between text-base font-bold pt-2 border-t border-[#d2d2d7]">
          <span className="text-[#1d1d1f]">Total</span>
          <span className="text-[#1d1d1f]">{formatPrice(total)}</span>
        </div>
      </div>
    </div>
  );

  // Address Card Component
  const AddressCard = ({ addr, idx, isSelected, onSelect, onDelete }) => (
    <button
      onClick={() => onSelect(idx)}
      className={`w-full text-left rounded-[32px] p-5 border-2 transition-all duration-200 cursor-pointer bg-white flex items-start gap-3 ${
        isSelected
          ? 'border-[#065DC0] bg-[#065DC0]/[0.02]'
          : 'border-[#d2d2d7] hover:border-[#86868b]'
      }`}
    >
      <MapPin size={18} className={`mt-0.5 shrink-0 ${isSelected ? 'text-[#065DC0]' : 'text-[#86868b]'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <p className="text-[15px] font-semibold text-[#1d1d1f]">{addr.firstName} {addr.lastName}</p>
          {addr.isDefault && (
            <span className="text-[10px] font-bold text-[#065DC0] bg-[#065DC0]/[0.08] px-2 py-0.5 rounded-full uppercase tracking-wider">Default</span>
          )}
          {(addr.source === 'billing' || addr.source === 'billing-secondary') && (
            <span className="text-[10px] font-bold text-[#6e6e73] bg-[#6e6e73]/[0.08] px-2 py-0.5 rounded-full uppercase tracking-wider">Billing Address</span>
          )}
        </div>
        <p className="text-sm text-[#6e6e73]">{addr.address}{addr.apartment ? `, ${addr.apartment}` : ''}</p>
        <p className="text-sm text-[#6e6e73]">{addr.city}, {addr.state} {addr.zip}</p>
        <p className="text-sm text-[#6e6e73]">{addr.country}</p>
        {addr.phone && <p className="text-sm text-[#6e6e73] mt-1">{addr.phone}</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {isSelected && <Check size={20} className="text-[#065DC0]" />}
        {addr.source === 'shipping' && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(idx); }}
            className="p-1.5 rounded-full hover:bg-red-50 transition-colors bg-transparent border-none cursor-pointer"
            title="Remove address"
          >
            <X size={14} className="text-[#86868b] hover:text-red-500" />
          </button>
        )}
      </div>
    </button>
  );

  // Address Form Component
  const AddressForm = () => (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-[#6e6e73] mb-1.5 block">First Name</label>
          <input type="text" required value={shippingInfo.firstName} onChange={e => setShippingInfo(p => ({...p, firstName: e.target.value}))}
            className="w-full px-4 py-3 rounded-[32px] border border-[#d2d2d7] text-sm text-[#1d1d1f] outline-none focus:border-[#065DC0] transition-colors bg-white" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#6e6e73] mb-1.5 block">Last Name</label>
          <input type="text" required value={shippingInfo.lastName} onChange={e => setShippingInfo(p => ({...p, lastName: e.target.value}))}
            className="w-full px-4 py-3 rounded-[32px] border border-[#d2d2d7] text-sm text-[#1d1d1f] outline-none focus:border-[#065DC0] transition-colors bg-white" />
        </div>
      </div>
      {[
        { key: 'email', label: 'Email', type: 'email' },
        { key: 'phone', label: 'Phone Number', type: 'tel' },
        { key: 'address', label: 'Street Address', type: 'text' },
        { key: 'apartment', label: 'Apartment, Suite, etc. (optional)', type: 'text', required: false },
        { key: 'city', label: 'City', type: 'text' },
      ].map(({ key, label, type, required: req }) => (
        <div key={key}>
          <label className="text-xs font-semibold text-[#6e6e73] mb-1.5 block">{label}</label>
          <input type={type} required={req !== false} value={shippingInfo[key]} onChange={e => setShippingInfo(p => ({...p, [key]: e.target.value}))}
            className="w-full px-4 py-3 rounded-[32px] border border-[#d2d2d7] text-sm text-[#1d1d1f] outline-none focus:border-[#065DC0] transition-colors bg-white" />
        </div>
      ))}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-[#6e6e73] mb-1.5 block">State / Province</label>
          <input type="text" required value={shippingInfo.state} onChange={e => setShippingInfo(p => ({...p, state: e.target.value}))}
            className="w-full px-4 py-3 rounded-[32px] border border-[#d2d2d7] text-sm text-[#1d1d1f] outline-none focus:border-[#065DC0] transition-colors bg-white" />
        </div>
        <div>
          <label className="text-xs font-semibold text-[#6e6e73] mb-1.5 block">ZIP / Postal Code</label>
          <input type="text" required value={shippingInfo.zip} onChange={e => setShippingInfo(p => ({...p, zip: e.target.value}))}
            className="w-full px-4 py-3 rounded-[32px] border border-[#d2d2d7] text-sm text-[#1d1d1f] outline-none focus:border-[#065DC0] transition-colors bg-white" />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-[#6e6e73] mb-1.5 block">Country / Region</label>
        <input type="text" readOnly value={shippingInfo.country}
          className="w-full px-4 py-3 rounded-[32px] border border-[#d2d2d7] text-sm text-[#6e6e73] outline-none bg-[#f5f5f7] cursor-not-allowed" />
      </div>

      {/* Save to account checkbox */}
      <label className="flex items-center gap-3 cursor-pointer py-2">
        <input
          type="checkbox"
          checked={saveNewToAccount}
          onChange={(e) => setSaveNewToAccount(e.target.checked)}
          className="w-4 h-4 rounded border-[#d2d2d7] text-[#065DC0] focus:ring-[#065DC0] cursor-pointer accent-[#065DC0]"
        />
        <span className="text-sm text-[#6e6e73]">Save this address to my FOZZIL account</span>
      </label>
    </div>
  );

  if (step === 'processing') {
    return (
      <div className="store-page bg-white min-h-screen flex items-center justify-center">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center max-w-[400px] px-6">
          <div className="relative mb-8 mx-auto w-fit">
            <div className="w-20 h-20 rounded-full border-[3px] border-[#065DC0]/20 border-t-[#065DC0] animate-spin" />
            <div className="absolute inset-0 flex items-center justify-center">
              <Lock size={24} className="text-[#065DC0]" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-[#1d1d1f] mb-2">Processing your payment...</h1>
          <p className="text-[15px] text-[#6e6e73]">Please wait while FOZZIL Pay securely routes to your chosen payment provider.</p>
          <div className="flex items-center justify-center gap-2 mt-4 bg-[#f5f5f7] py-2 px-4 rounded-full border border-black/[0.05]">
            <ShieldCheck size={14} className="text-[#065DC0]" />
            <p className="text-xs font-semibold text-[#1d1d1f]">FOZZIL Pay Gateway • {paymentMethod === 'card' ? 'Credit/Debit Card' : paymentMethod === 'upi' ? 'UPI' : 'Net Banking'}</p>
          </div>
        </motion.div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="store-page bg-white min-h-screen">
        <div className="max-w-[680px] mx-auto px-6 py-16">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2, damping: 15 }}
              className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center mx-auto mb-6"
            >
              <Check size={40} className="text-white" />
            </motion.div>
            <p className="text-sm text-[#065DC0] font-semibold mb-2">Thank you for your order</p>
            <h1 className="text-[32px] font-bold text-[#1d1d1f] mb-3">Order Confirmed</h1>
            <p className="text-[15px] text-[#6e6e73] mb-1">Order #{orderNumber}</p>
            <p className="text-[15px] text-[#6e6e73] mb-8">
              A confirmation email has been sent to <strong className="text-[#1d1d1f]">{shippingInfo.email}</strong>
            </p>

            <div className="bg-[#f5f5f7] rounded-[32px] p-6 text-left mb-8">
              <h3 className="text-base font-bold text-[#1d1d1f] mb-4">Order Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-[#d2d2d7]">
                  <span className="text-[#6e6e73]">Order Number</span>
                  <span className="font-semibold text-[#1d1d1f]">{orderNumber}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#d2d2d7]">
                  <span className="text-[#6e6e73]">Total Paid</span>
                  <span className="font-bold text-[#1d1d1f]">{formatPrice(total)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#d2d2d7]">
                  <span className="text-[#6e6e73]">Payment</span>
                  <span className="font-medium text-[#1d1d1f]">{paymentMethod === 'card' ? 'Card' : paymentMethod === 'upi' ? 'UPI' : 'Net Banking'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#d2d2d7]">
                  <span className="text-[#6e6e73]">Delivery</span>
                  <span className="font-medium text-[#1d1d1f]">{deliveryMethod === 'express' ? '2–3 business days' : '5–7 business days'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-[#6e6e73]">Ship to</span>
                  <span className="font-medium text-[#1d1d1f] text-right">{shippingInfo.firstName} {shippingInfo.lastName}<br />{shippingInfo.city}, {shippingInfo.state}</span>
                </div>
              </div>
            </div>

            <div className="bg-[#f5f5f7] rounded-[32px] p-6 text-left mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Truck size={20} className="text-[#065DC0]" />
                <div>
                  <p className="text-sm font-semibold text-[#1d1d1f]">Estimated Delivery</p>
                  <p className="text-sm text-[#6e6e73]">
                    {deliveryMethod === 'express'
                      ? new Date(Date.now() + 3 * 86400000).toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' })
                      : new Date(Date.now() + 7 * 86400000).toLocaleDateString('en-IN', { weekday: 'long', month: 'long', day: 'numeric' })
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Package size={20} className="text-[#065DC0]" />
                <div>
                  <p className="text-sm font-semibold text-[#1d1d1f]">Free setup & onboarding included</p>
                  <p className="text-sm text-[#6e6e73]">Our specialist team will contact you to schedule installation.</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/shop" className="px-8 py-3.5 rounded-full bg-[#065DC0] text-white text-[15px] font-semibold hover:bg-[#0077ed] transition-all text-center">
                Continue Shopping
              </Link>
              <Link to="/shop/orders" className="px-8 py-3.5 rounded-full border border-[#d2d2d7] text-[#1d1d1f] text-[15px] font-semibold hover:bg-[#f5f5f7] transition-all text-center">
                View Orders
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="store-page bg-[#f5f5f7] sm:bg-white min-h-screen flex flex-col transition-colors duration-400">
      {/* Secure Checkout Header */}
      <div className="border-b border-[#d2d2d7] bg-white sticky top-0 z-50">
        <div className="max-w-[1080px] mx-auto px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/shop/bag')} className="text-[#065DC0] text-sm font-medium flex items-center gap-1 bg-transparent border-none cursor-pointer hover:underline">
            <ChevronLeft size={16} /> Back to Bag
          </button>
          <div className="flex items-center gap-2">
            <Lock size={14} className="text-[#86868b]" />
            <span className="text-sm font-semibold text-[#1d1d1f]">Secure Checkout</span>
          </div>
          <div className="w-[100px]" />
        </div>
      </div>

      <div className="max-w-[1080px] mx-auto px-6 py-10">
        <StepIndicator />

        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-10">
          <div className="hidden lg:block">
            <OrderSummary />
          </div>

          <div>
            <AnimatePresence mode="wait">
              {/* STEP 1: Shipping */}
              {step === 'shipping' && (
                <motion.div key="shipping" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                  <h2 className="text-[24px] font-bold text-[#1d1d1f] mb-1">Where should we send your order?</h2>
                  <p className="text-[15px] text-[#6e6e73] mb-6">
                    {savedAddresses.length > 0 ? 'Select a saved address or add a new one.' : 'Enter your shipping address below.'}
                  </p>

                  {isLoadingAddresses ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-2 border-[#d2d2d7] border-t-[#065DC0] rounded-full animate-spin" />
                    </div>
                  ) : (
                    <form onSubmit={handleShippingSubmit}>
                      {/* Saved Addresses */}
                      {savedAddresses.length > 0 && (
                        <div className="space-y-3 mb-6">
                          {savedAddresses.map((addr, idx) => (
                            <AddressCard
                              key={addr.id || idx}
                              addr={addr}
                              idx={idx}
                              isSelected={selectedAddressIdx === idx && !showAddressForm}
                              onSelect={handleSelectAddress}
                              onDelete={deleteAddressFromSupabase}
                            />
                          ))}

                          {/* Add New Address Button */}
                          <button
                            type="button"
                            onClick={handleAddNewAddress}
                            className={`w-full text-left rounded-[32px] p-5 border-2 border-dashed transition-all duration-200 cursor-pointer bg-white flex items-center gap-3 ${
                              showAddressForm
                                ? 'border-[#065DC0] bg-[#065DC0]/[0.02]'
                                : 'border-[#d2d2d7] hover:border-[#86868b]'
                            }`}
                          >
                            <Plus size={18} className={showAddressForm ? 'text-[#065DC0]' : 'text-[#86868b]'} />
                            <span className={`text-[15px] font-semibold ${showAddressForm ? 'text-[#065DC0]' : 'text-[#6e6e73]'}`}>
                              Add a new address
                            </span>
                          </button>
                        </div>
                      )}

                      {/* Address Form (shown when adding new or no saved addresses) */}
                      {showAddressForm && <AddressForm />}

                      <button
                        type="submit"
                        disabled={isSavingAddress}
                        className="w-fit mt-6 px-8 py-3.5 mx-auto rounded-full bg-[#065DC0] text-white text-[15px] font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {isSavingAddress ? 'Saving address...' : 'Continue to Delivery'} <ChevronRight size={16} />
                      </button>
                    </form>
                  )}
                </motion.div>
              )}

              {/* STEP 2: Delivery */}
              {step === 'delivery' && (
                <motion.div key="delivery" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                  <div className="bg-[#f5f5f7] rounded-[32px] p-5 mb-6 flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <MapPin size={18} className="text-[#065DC0] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-[#1d1d1f]">Ships to</p>
                        <p className="text-sm text-[#6e6e73]">{shippingInfo.firstName} {shippingInfo.lastName}</p>
                        <p className="text-sm text-[#6e6e73]">{shippingInfo.address}{shippingInfo.apartment ? `, ${shippingInfo.apartment}` : ''}</p>
                        <p className="text-sm text-[#6e6e73]">{shippingInfo.city}, {shippingInfo.state} {shippingInfo.zip}</p>
                      </div>
                    </div>
                    <button onClick={() => setStep('shipping')} className="text-[#065DC0] text-sm font-medium bg-transparent border-none cursor-pointer hover:underline flex items-center gap-1">
                      <Edit3 size={14} /> Edit
                    </button>
                  </div>

                  <h2 className="text-[24px] font-bold text-[#1d1d1f] mb-1">How would you like your order delivered?</h2>
                  <p className="text-[15px] text-[#6e6e73] mb-6">Choose a delivery method below.</p>

                  <div className="space-y-3">
                    {[
                      { id: 'standard', label: 'Standard Delivery', desc: 'Delivers in 5–7 business days', note: 'FREE', icon: <Truck size={20} /> },
                      { id: 'express', label: 'Express Delivery', desc: 'Delivers in 2–3 business days', note: formatPrice(expressShippingCost) || 'Premium', icon: <Truck size={20} /> },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setDeliveryMethod(opt.id)}
                        className={`w-full text-left rounded-[32px] p-5 border-2 transition-all duration-200 cursor-pointer bg-white flex items-center gap-4 ${
                          deliveryMethod === opt.id
                            ? 'border-[#065DC0] bg-[#065DC0]/[0.03]'
                            : 'border-[#d2d2d7] hover:border-[#86868b]'
                        }`}
                      >
                        <div className={`${deliveryMethod === opt.id ? 'text-[#065DC0]' : 'text-[#6e6e73]'}`}>{opt.icon}</div>
                        <div className="flex-1">
                          <p className="text-[15px] font-semibold text-[#1d1d1f]">{opt.label}</p>
                          <p className="text-sm text-[#6e6e73]">{opt.desc}</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-sm font-semibold ${opt.note === 'FREE' ? 'text-green-600' : 'text-[#1d1d1f]'}`}>{opt.note}</span>
                        </div>
                        {deliveryMethod === opt.id && <Check size={20} className="text-[#065DC0] shrink-0" />}
                      </button>
                    ))}
                  </div>

                  <div className="mt-6 bg-[#f5f5f7] rounded-[32px] p-5 flex items-start gap-3">
                    <Package size={18} className="text-[#065DC0] mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-[#1d1d1f]">Free setup and onboarding included</p>
                      <p className="text-sm text-[#6e6e73]">Our specialist team will contact you after delivery to schedule professional installation and calibration at your location.</p>
                    </div>
                  </div>

                  <button onClick={handleDeliverySubmit} className="w-fit mt-6 px-8 py-3.5 mx-auto rounded-full bg-[#065DC0] text-white text-[15px] font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all flex items-center justify-center gap-2">
                    Continue to Payment <ChevronRight size={16} />
                  </button>
                </motion.div>
              )}

              {/* STEP 3: Payment */}
              {step === 'payment' && (
                <motion.div key="payment" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                  <div className="bg-[#f5f5f7] rounded-[32px] p-5 mb-6 flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <MapPin size={18} className="text-[#065DC0] mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-[#1d1d1f]">{shippingInfo.firstName} {shippingInfo.lastName} • {deliveryMethod === 'express' ? 'Express (2–3 days)' : 'Standard (5–7 days)'}</p>
                        <p className="text-sm text-[#6e6e73]">{shippingInfo.address}, {shippingInfo.city}, {shippingInfo.state} {shippingInfo.zip}</p>
                      </div>
                    </div>
                    <button onClick={() => setStep('shipping')} className="text-[#065DC0] text-sm font-medium bg-transparent border-none cursor-pointer hover:underline">Edit</button>
                  </div>

                  <h2 className="text-[24px] font-bold text-[#1d1d1f] mb-1">How would you like to pay?</h2>
                  <p className="text-[15px] text-[#6e6e73] mb-6">All transactions are encrypted and secure.</p>

                  <div className="space-y-3 mb-6">
                    {[
                      { id: 'card', label: 'Credit or Debit Card', desc: 'Visa, Mastercard, Rupay, American Express', icon: <FaCreditCard className="text-2xl text-[#6e6e73]" /> },
                      { id: 'upi', label: 'UPI', desc: 'Google Pay, PhonePe, Paytm, BHIM', icon: <img src="/fpay-logos/upi-logo.png" alt="UPI" className="w-8 h-8 object-contain mix-blend-multiply opacity-70" /> },
                      { id: 'bank', label: 'Net Banking', desc: 'All major banks supported', icon: <FaUniversity className="text-2xl text-[#6e6e73]" /> },
                    ]
                    .filter(opt => !(total > 99000 && opt.id === 'upi'))
                    .map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setPaymentMethod(opt.id)}
                        className={`w-full text-left rounded-[32px] p-5 border-2 transition-all duration-200 cursor-pointer bg-white flex items-center gap-4 ${
                          paymentMethod === opt.id
                            ? 'border-[#065DC0] bg-[#065DC0]/[0.03]'
                            : 'border-[#d2d2d7] hover:border-[#86868b]'
                        }`}
                      >
                        <div className="w-8 flex items-center justify-center shrink-0">
                          {opt.icon}
                        </div>
                        <div className="flex-1">
                          <p className="text-[15px] font-semibold text-[#1d1d1f]">{opt.label}</p>
                          <p className="text-sm text-[#6e6e73]">{opt.desc}</p>
                        </div>
                        {paymentMethod === opt.id && <Check size={20} className="text-[#065DC0] shrink-0" />}
                      </button>
                    ))}
                  </div>

                  <button onClick={handlePaymentSubmit} className="w-fit px-8 py-3.5 mx-auto rounded-full bg-[#065DC0] text-white text-[15px] font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all flex items-center justify-center gap-2">
                    Review Order <ChevronRight size={16} />
                  </button>

                  <div className="flex items-center justify-center gap-2 mt-4">
                    <ShieldCheck size={14} className="text-[#86868b]" />
                    <p className="text-xs text-[#86868b]">Secured by FOZZIL Pay • 256-bit encryption</p>
                  </div>
                </motion.div>
              )}

              {/* STEP 4: Review */}
              {step === 'review' && (
                <motion.div key="review" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
                  <h2 className="text-[24px] font-bold text-[#1d1d1f] mb-1">Review your order</h2>
                  <p className="text-[15px] text-[#6e6e73] mb-6">Please review the details below before placing your order.</p>

                  <div className="bg-[#f5f5f7] rounded-[32px] p-5 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-[#1d1d1f] flex items-center gap-2"><MapPin size={16} className="text-[#065DC0]" /> Shipping</h3>
                      <button onClick={() => setStep('shipping')} className="text-[#065DC0] text-sm font-medium bg-transparent border-none cursor-pointer hover:underline">Edit</button>
                    </div>
                    <p className="text-sm text-[#6e6e73]">{shippingInfo.firstName} {shippingInfo.lastName}</p>
                    <p className="text-sm text-[#6e6e73]">{shippingInfo.address}{shippingInfo.apartment ? `, ${shippingInfo.apartment}` : ''}</p>
                    <p className="text-sm text-[#6e6e73]">{shippingInfo.city}, {shippingInfo.state} {shippingInfo.zip}, {shippingInfo.country}</p>
                    <p className="text-sm text-[#6e6e73]">{shippingInfo.email} • {shippingInfo.phone}</p>
                  </div>

                  <div className="bg-[#f5f5f7] rounded-[32px] p-5 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-[#1d1d1f] flex items-center gap-2"><Truck size={16} className="text-[#065DC0]" /> Delivery</h3>
                      <button onClick={() => setStep('delivery')} className="text-[#065DC0] text-sm font-medium bg-transparent border-none cursor-pointer hover:underline">Edit</button>
                    </div>
                    <p className="text-sm text-[#6e6e73]">{deliveryMethod === 'express' ? 'Express — 2–3 business days' : 'Standard — 5–7 business days'}</p>
                    <p className="text-sm text-[#6e6e73]">Free setup & onboarding included</p>
                  </div>

                  <div className="bg-[#f5f5f7] rounded-[32px] p-5 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-[#1d1d1f] flex items-center gap-2"><CreditCard size={16} className="text-[#065DC0]" /> Payment</h3>
                      <button onClick={() => setStep('payment')} className="text-[#065DC0] text-sm font-medium bg-transparent border-none cursor-pointer hover:underline">Edit</button>
                    </div>
                    <p className="text-sm text-[#6e6e73]">
                      {paymentMethod === 'card' ? 'Credit or Debit Card' : paymentMethod === 'upi' ? 'UPI' : 'Net Banking'}
                    </p>
                  </div>

                  <div className="bg-[#f5f5f7] rounded-[32px] p-5 mb-6">
                    <h3 className="text-sm font-bold text-[#1d1d1f] mb-4">Items ({items.length})</h3>
                    {items.map(item => (
                      <div key={item.bagId} className="flex gap-4 py-3 border-b border-[#d2d2d7] last:border-none">
                        <img src={item.image} alt={item.name} className="w-16 h-16 object-contain rounded-lg bg-white p-1" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-[#1d1d1f]">{item.name}</p>
                          {item.config?.map(({label, value}) => (
                            <p key={label} className="text-xs text-[#6e6e73]">{value}</p>
                          ))}
                          <p className="text-xs text-[#6e6e73]">Qty: {item.quantity}</p>
                        </div>
                        <p className="text-sm font-semibold text-[#1d1d1f]">{formatPrice(item.price * item.quantity)}</p>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-[#d2d2d7] pt-4 mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#6e6e73]">Subtotal</span>
                      <span className="font-medium text-[#1d1d1f]">{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-[#6e6e73]">Shipping</span>
                      <span className="font-medium text-[#1d1d1f]">{shipping === 0 ? 'FREE' : formatPrice(shipping)}</span>
                    </div>
                    <div className="flex justify-between text-[21px] font-bold pt-3 border-t border-[#d2d2d7]">
                      <span className="text-[#1d1d1f]">Total</span>
                      <span className="text-[#1d1d1f]">{formatPrice(total)}</span>
                    </div>
                  </div>

                  <p className="text-xs text-[#86868b] mb-4 leading-relaxed">
                    By placing your order, you agree to FOZZIL's <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#065DC0] hover:underline">Terms of Use</Link>, <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#065DC0] hover:underline">Privacy Policy</Link>, and <Link to="/cancellation-and-refund" target="_blank" rel="noopener noreferrer" className="text-[#065DC0] hover:underline">Cancellation & Refund Policy</Link>.
                  </p>

                  <button onClick={handlePlaceOrder} className="w-fit px-8 py-3.5 mx-auto rounded-full bg-[#065DC0] text-white text-base font-semibold border-none cursor-pointer hover:bg-[#0077ed] transition-all flex items-center justify-center gap-2">
                    <Lock size={16} /> Place Order — {formatPrice(total)}
                  </button>

                  <div className="flex items-center justify-center gap-2 mt-4">
                    <ShieldCheck size={14} className="text-[#86868b]" />
                    <p className="text-xs text-[#86868b]">Secured by FOZZIL Pay • 256-bit encryption</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Ecosystem-style Checkout Footer */}
      <div className="bg-[#0a0a0a] border-t border-white/[0.05] mt-auto w-full">
        <div className="max-w-[1080px] mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-[12px] text-[#86868b] tracking-wide font-medium">© {new Date().getFullYear()} FOZZIL</span>
          <div className="flex items-center gap-6 md:gap-8 text-[12px] text-[#86868b] font-medium">
            <a href="https://ecosystem.fozzil.in" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Ecosystem User Guide</a>
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>

      <FozzilPayCheckout
        isOpen={showFozzilPay}
        onClose={() => setShowFozzilPay(false)}
        amount={total.toString()}
        productinfo={items.length === 1 ? items[0].name : `FOZZIL Store Order (${items.length} items)`}
        isRecurring={false}
        user_id={user?.id || 'guest'}
        firstname={shippingInfo.firstName || (user?.user_metadata?.full_name?.split(' ')[0]) || 'Customer'}
        email={shippingInfo.email}
        phone={shippingInfo.phone || '0000000000'}
        onSuccess={handlePaymentSuccess}
        allowedMethods={total > 99000 ? ['Card', 'Netbanking', 'Wallet'] : ['UPI', 'Card', 'Netbanking', 'Wallet']}
        initialMethod={paymentMethod}
      />
    </div>
  );
};

export default CheckoutPage;
